// components/ExportToExcelButton.tsx
import { FC } from "react";
import ExcelJS from "exceljs";
import { saveAs } from "file-saver";
import { client } from "app/reducers/client/clientDataInterfce";
import {
  taskData,
  taskPayments,
  taskReceipts,
} from "../task/taskDataInterface";
import {
  convert,
  getDetailsAgainstTransaction,
  getStatus,
} from "app/helpers/helperFunction";
import { PAYMENT_STATUS, TRANSACTION_TYPE } from "app/helpers/constants";

interface ExportToExcelButtonProps {
  data: any[] | any | undefined;
  headers: { columnName: string; id: string }[];
  fileName?: string;
}

const ExportToExcelButton: FC<ExportToExcelButtonProps> = ({
  data,
  headers,
  fileName = "Export",
}) => {
  const handleExport = async () => {
    const workbook = new ExcelJS.Workbook();
    const worksheet = workbook.addWorksheet("Sheet 1");

    // Header row
    const headerRow = worksheet.addRow(headers.map((h) => h.columnName));
    headerRow.eachCell((cell) => {
      cell.font = { bold: true };
    });
    worksheet.views = [{ state: "frozen", ySplit: 1 }];

    // Data rows
    data?.forEach((item: Record<string, any>) => {
      const row: (string | null)[] = headers.map(
        (h: { columnName: string; id: string }) => {
          if (h.id === "NULL") {
            // Custom columns
            if (h.columnName === "Contacts") {
              const contacts = item.ContactPersons?.slice(0, 2) || [];
              return contacts.length
                ? contacts
                    .map(
                      (c: { Name: string; MobileNumber: any }) =>
                        `${c.Name ?? "-"} (${c.MobileNumber ?? "-"})`
                    )
                    .join(" | ")
                : "-";
            }
            if (h.columnName === "Coordinator 1") {
              return item.CoordinatorOneName ?? "-";
            }
            if (h.columnName === "Coordinator 2") {
              return item.CoordinatorTwoName ?? "-";
            }
            return "-";
          }

          // Task-specific custom handling
          switch (h.columnName) {
            case "Start Date":
            case "Due Date":
              return item[h.id] ? convert(item[h.id]) : "-";

            case "Article":
            case "Managed By":
              return item[h.id] ?? "-";

            case "Task Type":
              return item.IsRepeatative ? "Repetitive" : "One Time Task";

            case "Billing Status":
              return PAYMENT_STATUS[item[h.id]] ?? "-";

            case "Work Status": {
              const statusObj = getStatus(item.StatusValue);
              return statusObj?.name ?? "-";
            }

            case "Status": {
              const value = item[h.id];
              if (value === true) return "✅ Active";
              if (value === false) return "❌ Inactive";
              return "-";
            }

            // Transaction report cases
            case "Type":
              return item?.Type ? TRANSACTION_TYPE[Number(item.Type)] : "-";

            case "Narration": {
              const details =
                item.Type !== TRANSACTION_TYPE.Other
                  ? getDetailsAgainstTransaction(
                      item as taskPayments | taskReceipts
                    ).value
                  : "";
              const narration = item?.Narration || "-";
              return details !== "-" || ""
                ? `${details} | ${narration}`
                : narration;
            }

            case "Client Name":
              return item[h.id] ?? "-";

            case "Transaction Date":
              return item?.TransactionDate
                ? convert(item.TransactionDate)
                : "-";

            case "Amount":
              if (item?.Amount !== null && item?.Amount !== undefined) {
                return item.IsPayment ? -item.Amount : item.Amount;
              }
              return "-";

            // Invoice report cases
            case "Tasks":
              return (
                item.Tasks.map(
                  (task: { TaskCode: string }) => task.TaskCode
                ).join(",") || "-"
              );

            case "Firm":
              return item.Firm.Name;

            case "Invoice Date":
              return item[h.id] ? convert(item[h.id]) : "-";

            case "Group":
              return item.GroupName ?? "-";

            case "Due Amount":
              return item.TotalAmount - item.PaidAmount;

            default:
              return item[h.id as keyof typeof item] ?? "-";
          }
        }
      );

      const addedRow = worksheet.addRow(row);

      // Apply font color styling
      row.forEach((cellValue: string | null, index: number) => {
        const colName = headers[index].columnName;
        const cell = addedRow.getCell(index + 1);

        if (colName === "Status" && typeof cellValue === "string") {
          if (cellValue.includes("Active")) {
            cell.font = { color: { argb: "FF228B22" } };
          } else if (cellValue.includes("Inactive")) {
            cell.font = { color: { argb: "FFFF0000" } };
          }
        }
        if (colName === "Amount") {
          if (typeof cellValue === "number") {
            if (cellValue > 0) {
              cell.font = { color: { argb: "FF228B22" } };
            } else if (cellValue < 0) {
              cell.font = { color: { argb: "FFFF0000" } };
            }
          }
        }
        if (colName === "Work Status" && typeof cellValue === "string") {
          const statusColors: Record<string, string> = {
            "Assigned": "FFD1E8FF",            
            "In Progress": "FFF8F0B2",        
            "Sent for Check": "FFF1C77B",      
            "Pending from Client": "FFC1A6F9", 
            "Completed": "FF94F294",           
            "Approved": "FF49E349",            
            "On Hold": "FFD35C5C",           
            "Tax Pending": "FFCDA292",         
            "Rejected": "FFFF6F6F",            
            "Completion Approved": "FF82C367", 
          };
      
          const color = statusColors[cellValue] ?? "FFE4DEDE"; // Default black
          //cell.font = { color: { argb: color } };
          cell.fill = {
            type: 'pattern',
            pattern: 'solid',
            fgColor: { argb: color }
          };
          cell.border = {
            top: { style: 'thin' },
            left: { style: 'thin' },
            bottom: { style: 'thin' },
            right: { style: 'thin' }
          };
        }
      });
    });

    // Add total row below the data rows
    const totalRowValues = headers.map((h) => {
      if (["Total Amount", "Due Amount"].includes(h.columnName)) {
        const colIndex = headers.indexOf(h) + 1;
        const startRow = 2;
        const endRow = worksheet.lastRow?.number;
        return {
          formula: `SUM(${worksheet.getColumn(colIndex).letter}${startRow}:${
            worksheet.getColumn(colIndex).letter
          }${endRow})`,
        };
      }
      return "";
    });

    const totalRow = worksheet.addRow(totalRowValues);

    totalRow.font = { bold: true };

    // Auto-width columns
    worksheet.columns.forEach((column) => {
      let maxLength = 10;
      column?.eachCell?.({ includeEmpty: true }, (cell) => {
        const length = cell.value?.toString().length ?? 0;
        if (length > maxLength) maxLength = length;
      });
      column.width = maxLength + 2;
    });

    // Save
    const buffer = await workbook.xlsx.writeBuffer();
    saveAs(new Blob([buffer]), `${fileName}.xlsx`);
  };

  return (
    <button className="btn btn-sm btn-primary" onClick={handleExport}>
      Export Data
    </button>
  );
};

export default ExportToExcelButton;
